import React, { useEffect, useState } from 'react';

import { AppSchemasComplianceSummaryComplianceReport } from '@ariksa/compliance-policies/api';
import {
  Box,
  Center,
  Checkbox,
  HStack,
  Text,
  UseDisclosureProps,
} from '@chakra-ui/react';
import { each, filter, includes, toArray } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { useSelector, useDispatch } from 'react-redux';

import {
  renderSeverityBar,
  renderSeverityBubble,
  renderStatusTag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { ActionButton } from 'components/DataEntry';
import { ExceptionIcon, IconTypes } from 'components/Icons';
import { ExcludePoliciesForResource } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Alerts/ExcludePoliciesForResources';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { useCompliance } from 'containers/Compliance/Components/hooks/context';
import { SeverityComponent } from 'containers/Compliance/Components/SeverityComponent';

interface Props {
  selectedResource: Record<string, any>;
  selectedBlueprint: Record<string, any>;
  tableStyles?: any;
  showRuleId?: boolean;
  fromCompliance?: boolean;
  excludePoliciesDisclosure: UseDisclosureProps;
  selectedPolicies: string[];
  setSelectedPolicies(values: string[]);
  getRules: () => void;
}

export const RulesForResource: React.FC<Props> = props => {
  const {
    selectedResource,
    tableStyles,
    selectedBlueprint,
    showRuleId = true,
    fromCompliance = false,
    excludePoliciesDisclosure,
    selectedPolicies,
    setSelectedPolicies,
    getRules,
  } = props;
  const dispatch = useDispatch();
  const { rules } = useSelector(selectActiveResource);

  const { isOpenSeverityModal, renderResourceActions } = useCompliance();
  const [allPolicies, setAllPolicies] = useState<
    Record<string, AppSchemasComplianceSummaryComplianceReport>
  >({});

  useEffect(() => {
    if (!isEmpty(rules.data)) {
      let items: Record<
        string,
        AppSchemasComplianceSummaryComplianceReport
      > = {};
      each(
        rules.data,
        o =>
          (items[
            o.rule_id!
          ] = o as AppSchemasComplianceSummaryComplianceReport),
      );
      setAllPolicies(items);
    }
  }, [rules.data]);

  const ruleId = {
    header: 'Rule Id',
    accessor: 'name',
    align: 'left',
    styles: { width: '400px', cell: { maxWidth: '400px' } },
  };

  const description = {
    header: (
      <HStack>
        <Checkbox
          isChecked={
            !!selectedPolicies?.length &&
            selectedPolicies?.length === rules.data?.length
          }
          isDisabled={rules.isLoading}
          onChange={e => {
            const values = e.target.checked
              ? map(rules.data, o => o.rule_id)
              : [];
            setSelectedPolicies(values);
          }}
          isIndeterminate={
            !!selectedPolicies?.length &&
            selectedPolicies?.length !== rules.data?.length
          }
        />
        <Box pl={20}>Policy Description</Box>
      </HStack>
    ),
    accessor: 'description',
    align: 'left',
    render: ({ value, row }) => {
      const isChecked = includes(selectedPolicies, row?.rule_id);
      return (
        <HStack>
          <Checkbox
            isChecked={isChecked}
            key={`${row.rule_id}`}
            onChange={e => {
              e.stopPropagation();
              // Toggle: add when unchecked, remove when checked (the branches
              // were inverted, so the checkbox never changed the selection).
              let values: string[] = [];
              if (!isChecked) {
                let items = new Set<string>(selectedPolicies);
                items.add(row.rule_id);
                values = toArray(items) as string[];
              } else values = filter(selectedPolicies, o => o !== row.rule_id);
              setSelectedPolicies(values);
            }}
          />
          <Box pl={8}>
            <WithResourceIcon resourceType={IconTypes.Policy}>
              <Text whiteSpace="wrap" noOfLines={2}>
                {value}
              </Text>
            </WithResourceIcon>
          </Box>
        </HStack>
      );
    },
    styles: { width: '70%', cell: { maxWidth: '70%' } },
  };

  const restColumns = [
    {
      header: 'Status',
      accessor: 'status',
      render: ({ value }) => {
        return renderStatusTag(value === 'Rule_Fail' ? 'FAIL' : 'PASS');
      },
      styles: { width: '13%', cell: { maxWidth: '13%' } },
    },
    {
      header: 'Severity',
      accessor: 'severity',
      align: 'left',
      styles: { width: '10%', cell: { maxWidth: '10%' } },
      render: renderSeverityBar,
    },
    {
      header: 'Actions',
      accessor: '',
      render: ({ row }) => (
        <Center>
          <ActionButton
            icon={<ExceptionIcon />}
            label="Apply ecxeption"
            onClick={e => {
              setSelectedPolicies([row?.rule_id]);
              excludePoliciesDisclosure?.onOpen?.();
            }}
          />
        </Center>
      ),
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  return (
    <>
      <Table
        data={rules.data}
        columns={
          showRuleId
            ? [description, ruleId, ...restColumns]
            : [description, ...restColumns]
        }
        isLoading={rules.isLoading}
        pagination={{
          totalCount: rules.page.totalCount,
          pageInfo: rules.page.info,
          onChange: pageInfo => {
            dispatch(actions.updateRulesPageInfo(pageInfo));
          },
        }}
        onRowClick={row => {
          const values = !includes(selectedPolicies, row?.rule_id)
            ? [...selectedPolicies, row.rule_id]
            : filter(selectedPolicies, o => o !== row.rule_id);
          setSelectedPolicies(values);
        }}
        styles={tableStyles}
      />
      {isOpenSeverityModal && !fromCompliance && (
        <SeverityComponent
          resource={selectedResource}
          blueprint={selectedBlueprint}
        />
      )}
      {excludePoliciesDisclosure.isOpen && (
        <ExcludePoliciesForResource
          isOpen={excludePoliciesDisclosure.isOpen}
          onClose={excludePoliciesDisclosure.onClose!}
          selectedPolicies={map(selectedPolicies, o => allPolicies[o])}
          blueprintId={selectedBlueprint?.id}
          onSuccess={() => {
            getRules();
            setSelectedPolicies([]);
          }}
          totalPolicies={rules.page.totalCount}
        />
      )}
    </>
  );
};
